import type { ReactNode } from "react";
import type { FernieFaceState } from "~/components/fernie/fernieMarkRules";

import { Box, type SxProps, type Theme, useMediaQuery } from "@mui/material";
import { useEffect, useReducer } from "react";

import { FERNIE_FACE_VIEWBOX, FERNIE_SPROUT_PATH, FERNIE_TILE_PATH } from "~/components/fernie/fernieArt";
import {
	faceMachineReducer,
	GRIN_DURATION_MS,
	initFaceMachine,
	resolveAnimatedFaceWidth,
	resolveFaceAnimation
} from "~/components/fernie/fernieFaceAnimation";
import { fernieColors } from "~/themes/fernieBrand";

const FACE_ASPECT = 58 / 48;
const REDUCED_MOTION_QUERY = "(prefers-reduced-motion: reduce)";
const EYE_GLOW = `drop-shadow(0 0 2.5px ${fernieColors.led})`;

const EYE_TRANSFORM: Record<FernieFaceState, string> = {
	done: "scaleY(0.32)",
	listening: "translateY(-1px) scaleY(1.28)",
	rest: "scaleY(1)",
	straight: "scaleY(0.3)",
	thinking: "scaleY(0.85)",
	working: "scaleY(0.45)"
};

const usePrefersReducedMotion = () => useMediaQuery(REDUCED_MOTION_QUERY, { noSsr: true });

const useDisplayedFaceState = (state: FernieFaceState): FernieFaceState => {
	const [machine, dispatch] = useReducer(faceMachineReducer, state, initFaceMachine);

	useEffect(() => {
		dispatch({ type: "request", state });
	}, [state]);

	useEffect(() => {
		if (machine.displayed !== "done") return;
		const timer = window.setTimeout(() => dispatch({ type: "grinComplete" }), GRIN_DURATION_MS);
		return () => window.clearTimeout(timer);
	}, [machine.displayed]);

	return machine.displayed;
};

const renderMouth = (state: FernieFaceState, color: string): ReactNode => {
	switch (state) {
		case "done":
			return (
				<path
					className="fernie-mouth"
					d="M17.5 31.5 Q24 37.5 30.5 31.5"
					stroke={color}
					strokeWidth="2.6"
					strokeLinecap="round"
					fill="none"
				/>
			);
		case "thinking":
			return <circle className="fernie-mouth" cx="24" cy="34" r="1.4" fill={color} opacity="0.7" />;
		case "working":
			return (
				<path
					className="fernie-mouth"
					d="M20 33.5 H28"
					stroke={color}
					strokeWidth="2.4"
					strokeLinecap="round"
					fill="none"
				/>
			);
		case "straight":
			return (
				<path
					className="fernie-mouth"
					d="M21 33.5 H27"
					stroke={color}
					strokeWidth="2.2"
					strokeLinecap="round"
					fill="none"
				/>
			);
		default:
			return (
				<path
					className="fernie-mouth"
					d="M19 33 Q24 36.5 29 33"
					stroke={color}
					strokeWidth="2.4"
					strokeLinecap="round"
					fill="none"
				/>
			);
	}
};

interface FernieFaceAnimatedProps {
	state?: FernieFaceState;
	size?: number;
	decorative?: boolean;
	title?: string;
	sx?: SxProps<Theme>;
}

const FernieFaceAnimated = ({
	state = "rest",
	size = 40,
	decorative = false,
	title = "fernie",
	sx
}: FernieFaceAnimatedProps) => {
	const reducedMotion = usePrefersReducedMotion();
	const displayed = useDisplayedFaceState(state);
	const width = resolveAnimatedFaceWidth(size);
	const animation = resolveFaceAnimation(displayed, reducedMotion);

	const eyeAnimation = animation.blink
		? "fernieBlink 4.2s ease-in-out infinite"
		: animation.eyeScan
			? "fernieScan 1.8s ease-in-out infinite"
			: "none";
	const sproutAnimation =
		animation.sproutPulse === "soft"
			? "fernieSproutSoft 2.4s ease-in-out infinite"
			: animation.sproutPulse === "hard"
				? "fernieSproutHard 0.9s ease-in-out infinite"
				: "none";
	const mouthAnimation = animation.grin ? "fernieGrin 0.5s ease-out 1" : "none";
	const motion = reducedMotion ? "none" : "transform 0.25s ease, opacity 0.25s ease";

	return (
		<Box
			component="svg"
			role="img"
			aria-hidden={decorative || undefined}
			aria-label={decorative ? undefined : title}
			viewBox={FERNIE_FACE_VIEWBOX}
			xmlns="http://www.w3.org/2000/svg"
			sx={{
				display: "block",
				height: width * FACE_ASPECT,
				width,
				"@keyframes fernieBlink": {
					"0%, 86%, 100%": { transform: "scaleY(1)" },
					"91%, 95%": { transform: "scaleY(0.12)" }
				},
				"@keyframes fernieScan": {
					"0%, 100%": { transform: "translateX(-1.4px) scaleY(0.85)" },
					"50%": { transform: "translateX(1.4px) scaleY(0.85)" }
				},
				"@keyframes fernieSproutSoft": {
					"0%, 100%": { opacity: 0.85, transform: "scale(1)" },
					"50%": { opacity: 1, transform: "scale(1.05)" }
				},
				"@keyframes fernieSproutHard": {
					"0%, 100%": { opacity: 0.7, transform: "scale(0.95)" },
					"50%": { opacity: 1, transform: "scale(1.12)" }
				},
				"@keyframes fernieGrin": {
					"0%": { opacity: 0.4, transform: "scaleY(0.2)" },
					"60%": { transform: "scaleY(1.15)" },
					"100%": { opacity: 1, transform: "scaleY(1)" }
				},
				"& .fernie-sprout": {
					transformBox: "fill-box",
					transformOrigin: "50% 100%",
					animation: sproutAnimation
				},
				"& .fernie-eyes": {
					transformBox: "fill-box",
					transformOrigin: "center",
					transform: EYE_TRANSFORM[displayed],
					transition: motion,
					animation: eyeAnimation
				},
				"& .fernie-mouth": {
					transformBox: "fill-box",
					transformOrigin: "center",
					transition: motion,
					animation: mouthAnimation
				},
				...sx
			}}
		>
			<path className="fernie-sprout" d={FERNIE_SPROUT_PATH} fill={animation.ledColor} />
			<g transform="translate(0 10)">
				<path d={FERNIE_TILE_PATH} fill={fernieColors.tile} />
				<g style={animation.glow ? { filter: EYE_GLOW } : undefined}>
					<g className="fernie-eyes">
						<rect x="14.5" y="19" width="5" height="7" rx="2.5" fill={animation.ledColor} />
						<rect x="28.5" y="19" width="5" height="7" rx="2.5" fill={animation.ledColor} />
					</g>
					{renderMouth(displayed, animation.ledColor)}
				</g>
			</g>
		</Box>
	);
};

export default FernieFaceAnimated;
